/** Écran de jeu : carte mission, canvas, statut, onglets de défis (étoiles), carte de victoire. */
import type { EngineStatus } from '@ordre-cache/shared';
import { DIFF_IC, DIFFS, NB_DEFIS } from '@ordre-cache/shared';
import { curN, G, state } from '../../state';
import { isDefiUnlocked, progOf } from '../../storage';
import { bestStars } from '../../score';
import { RULES } from '../../rules-content';
import { computeBoardSize, setupCanvas } from '../board';
import { updateMeta } from '../meta';

export interface WinGain {
  pid: number;
  pts: number;
  stars: number;
  first: boolean;
}

export interface PlayDeps {
  canvas: HTMLCanvasElement;
  checkWin(st: EngineStatus): void;
  getLastGain(): WinGain | null;
}

const $ = (id: string): HTMLElement | null => document.getElementById(id);

/** Pose les variables d'accent du jeu courant (hex + alphas hex, offline-safe). */
function applyAccent(accent: string): void {
  const scr = $('scrPlay');
  if (scr && scr.dataset.ac !== accent) {
    scr.dataset.ac = accent;
    scr.style.setProperty('--ac', accent);
    scr.style.setProperty('--ac-soft', accent + '24');
    scr.style.setProperty('--ac-glow', accent + '66');
    document.documentElement.style.setProperty('--ac-aurora', accent);
  }
}

function fmtTime(ms: number): string {
  const m = Math.floor(ms / 60000);
  const s = Math.floor(ms / 1000) % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Rendu complet de l'écran de jeu. Le puzzle courant est garanti par l'appelant. Renvoie le statut. */
export function renderPlay(deps: PlayDeps): EngineStatus {
  const g = G();
  const p = state.puz;
  if (!p) return { ok: false, txt: '' };

  applyAccent(g.accent);

  const title = $('playTitle');
  if (title?.firstChild) title.firstChild.textContent = `${g.icon} ${g.name} `;
  const sub = $('playSub');
  if (sub) sub.textContent = `${DIFF_IC[state.diff]} ${DIFFS[state.diff]} · ${curN()}×${curN()}`;

  // ---- Carte mission (objectif du défi, bien visible au-dessus du plateau) ----
  const d = g.defis[state.defi];
  const mIcon = $('mIcon');
  if (mIcon) mIcon.textContent = d.ic ?? '🎯';
  const mName = $('mName');
  if (mName) mName.textContent = d.n;
  const mChip = $('mChipDefi');
  if (mChip) mChip.textContent = `Défi ${state.defi + 1}/${NB_DEFIS}`;
  const mPar = $('mChipPar');
  if (mPar) {
    mPar.textContent = p.par ? `Par ${p.par}` : '';
    mPar.classList.toggle('hidden', !p.par);
  }
  const mDesc = $('mDesc');
  if (mDesc) mDesc.textContent = d.d;
  const mMore = $('mMore');
  if (mMore) mMore.innerHTML = RULES[g.id].but;

  const sz = $('playSize');
  if (sz && sz.dataset.g !== g.id) {
    sz.dataset.g = g.id;
    sz.innerHTML = '';
    g.sizes.forEach((n, i) => {
      const b = document.createElement('button');
      b.textContent = n + '×' + n;
      b.dataset.s = String(i);
      sz.appendChild(b);
    });
  }
  if (sz) {
    [...sz.children].forEach((b) => {
      const el = b as HTMLElement;
      el.classList.toggle('on', Number(el.dataset.s) === state.sizeIdx);
    });
  }
  const df = $('playDiff');
  if (df) {
    [...df.children].forEach((b) => {
      const el = b as HTMLElement;
      el.classList.toggle('on', Number(el.dataset.d) === state.diff);
    });
  }

  // Statut + attribution de la victoire AVANT les onglets et la carte de victoire :
  // checkWin met à jour progression/score/lastGain, que le rendu ci-dessous consomme.
  const st = g.status(p);
  deps.checkWin(st);
  const gainNow = deps.getLastGain();

  // ---- Onglets de défis : parcours à déblocage — icône + étoiles, ou 🔒 anonyme ----
  const dots = $('defiDots');
  if (dots) {
    dots.innerHTML = '';
    const prog = progOf(g.id, state.diff);
    for (let i = 0; i < NB_DEFIS; i++) {
      const unlocked = isDefiUnlocked(g.id, state.diff, i);
      const st = bestStars(g.id, state.diff, i);
      const tab = document.createElement('button');
      tab.className =
        'defiTab' +
        (prog[i] ? ' done' : '') +
        (i === state.defi ? ' cur' : '') +
        (unlocked ? '' : ' locked');
      tab.dataset.i = String(i);
      if (!unlocked) {
        // Défi verrouillé : anonyme — son contenu reste un mystère jusqu'au déblocage.
        tab.setAttribute('aria-disabled', 'true');
        tab.setAttribute(
          'aria-label',
          `Défi ${i + 1}/${NB_DEFIS} — verrouillé : réussis d'abord le défi ${i}`,
        );
        tab.innerHTML = `<span class="dIc">🔒</span><span class="dStars">···</span>`;
      } else {
        const nm = g.defis[i].n;
        tab.setAttribute(
          'aria-label',
          `Défi ${i + 1}/${NB_DEFIS} — ${nm}${st ? `, ${st} étoile${st > 1 ? 's' : ''}` : ''}`,
        );
        tab.innerHTML = `<span class="dIc">${g.defis[i].ic ?? '•'}</span><span class="dStars"><i>${'★'.repeat(st)}</i>${'☆'.repeat(3 - st)}</span>`;
        // Défi tout juste débloqué par la victoire en cours : pop de déblocage.
        if (
          gainNow &&
          gainNow.first &&
          gainNow.pid === p.pid &&
          i === state.defi + 1 &&
          !prog[i]
        )
          tab.classList.add('justUnlocked');
      }
      dots.appendChild(tab);
    }
  }

  const area = document.querySelector<HTMLElement>('.boardArea');
  const size = area ? computeBoardSize(area) : 300;
  const ctx = setupCanvas(deps.canvas, size);
  g.draw(p, ctx, { size });

  const sb = $('statusBar');
  if (sb) {
    sb.className = 'statusBar' + (st.ok ? ' ok' : '');
    sb.innerHTML = st.txt;
  }

  // ---- Carte de victoire : SOUS le plateau, après un temps de contemplation ----
  // La solution reste visible ; la carte remplace statut/chips/contrôles (winMode)
  // et n'apparaît qu'après ~750 ms pour laisser le joueur VOIR ce qu'il a construit.
  const wc = $('winCard');
  const showWin = st.ok && !!gainNow && gainNow.pid === p.pid;
  document.querySelector<HTMLElement>('.sidePanel')?.classList.toggle('winMode', showWin);
  if (wc) {
    if (!showWin) {
      wc.classList.add('hidden');
      wc.classList.remove('show');
      delete wc.dataset.pid;
    } else if (gainNow) {
      if (wc.dataset.pid !== String(p.pid)) {
        wc.dataset.pid = String(p.pid);
        wc.classList.add('hidden');
        wc.classList.remove('show');
        window.setTimeout(() => {
          if (wc.dataset.pid === String(p.pid)) {
            wc.classList.remove('hidden');
            wc.classList.add('show');
            // Le plateau se recale sur la place restante (main.ts écoute resize).
            window.dispatchEvent(new Event('resize'));
          }
        }, 750);
      }
      wc.dataset.stars = String(gainNow.stars);
      const wt = $('winTitle');
      if (wt) wt.innerHTML = st.txt;
      const wp = $('winPts');
      if (wp) wp.innerHTML = `+${gainNow.pts} pts${gainNow.first ? '' : ' <small>(rejoué)</small>'}`;
      const wd = $('winDetail');
      if (wd) {
        const bits = [`👆 ${p.mv ?? 0} coup${(p.mv ?? 0) > 1 ? 's' : ''}`];
        if (p.par) bits.push(`par ${p.par}`);
        bits.push(`⏱ ${fmtTime(p._ft ?? 0)}`);
        wd.textContent = bits.join(' · ');
      }
      const wn = $('winNext');
      if (wn)
        wn.textContent =
          state.defi < NB_DEFIS - 1
            ? gainNow.first
              ? '🔓 Défi suivant ▶'
              : 'Défi suivant ▶'
            : '🧩 Retour aux jeux';
    }
  }

  updateMeta(st.ok);

  const ex = g.extra?.(p) ?? null;
  const btnNew = $('btnNew');
  if (btnNew) {
    btnNew.textContent = ex ? ex.label : '🔀 Nouveau';
    btnNew.dataset.extra = ex ? '1' : '';
  }
  return st;
}
